import { useElementSize } from './useElementSize';
import type { ExerciseLift, AxisTick } from './useLiftTrends';

export interface ExerciseHistoryChartProps {
  title: string;
  color: string;
  /** Every logged lift, oldest first, positioned by `t` across the date domain. */
  lifts: ExerciseLift[];
  /** Candidate week boundaries; as many are labelled as fit the width. */
  weekTicks: AxisTick[];
  selectedLiftId?: string;
  /** Fired on tap, with the dot's pixel position inside the plot for the popup. */
  onSelectLift?: (lift: ExerciseLift, x: number, y: number) => void;
}

const PAD_L = 40;
const PAD_R = 14;
const PAD_T = 18;
const PAD_B = 22;

/** Touch target radius. Dots are 3-6px, far below the ~44px a finger needs. */
const HIT_R = 15;

/** Half the widest the detail popup gets; the anchor is kept this far from each
 *  edge so a popup on the first or last dot cannot overflow the chart. */
const POPUP_HALF_W = 80;

/** Roughly how wide a "8/31" label renders at 10px, plus breathing room. */
const TICK_LABEL_W = 34;
/** Enough room for a three-digit value at 9px without dots touching. */
const VALUE_LABEL_W = 26;
const VALUE_LABEL_H = 11;

/** Week strides to try, so thinned ticks still land on a regular cadence. */
const TICK_STRIDES = [1, 2, 3, 4, 6, 8, 13, 26] as const;

export function ExerciseHistoryChart({
  title,
  color,
  lifts,
  weekTicks,
  selectedLiftId,
  onSelectLift,
}: ExerciseHistoryChartProps) {
  const [plotRef, { width: w, height: h }] = useElementSize<HTMLDivElement>();

  const hasData = lifts.length > 0;
  const ready = hasData && w > 0 && h > 0;

  const ariaLabel = hasData
    ? `${title}: ${lifts.length} lifts. ` +
      lifts
        .map(
          (l) =>
            `${l.dateLabel}, ${l.loadLabel} for ${l.reps} ${l.reps === 1 ? 'rep' : 'reps'}, ` +
            `estimated 1RM ${Math.round(l.value)} pounds${l.isMonthBest ? ', best of the month' : ''}`,
        )
        .join('. ') +
      '.'
    : `${title}: no data yet.`;

  const renderPlot = () => {
    const plotW = Math.max(1, w - PAD_L - PAD_R);
    const plotH = Math.max(1, h - PAD_T - PAD_B);
    const xFor = (t: number) => PAD_L + t * plotW;

    const values = lifts.map((l) => l.value);
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (min === max) {
      const pad = Math.max(10, Math.round(max * 0.1));
      min = Math.max(0, min - pad);
      max = max + pad;
    } else {
      const pad = (max - min) * 0.18;
      min = Math.max(0, min - pad);
      max = max + pad;
    }
    const yFor = (v: number) => PAD_T + (1 - (v - min) / (max - min)) * plotH;

    const gridLines = Math.min(6, Math.max(3, Math.round(plotH / 70) + 1));
    const gridVals = Array.from(
      { length: gridLines },
      (_, i) => max - ((max - min) * i) / (gridLines - 1),
    );

    // Keep every Nth week label, picking the smallest stride whose labels fit.
    const maxTicks = Math.max(2, Math.floor(plotW / TICK_LABEL_W));
    const stride =
      TICK_STRIDES.find((s) => Math.ceil(weekTicks.length / s) <= maxTicks) ??
      TICK_STRIDES[TICK_STRIDES.length - 1];
    const ticks = weekTicks.filter((_, i) => i % stride === 0);

    const bests = lifts.filter((l) => l.isMonthBest);
    const linePoints = bests.map((l) => `${xFor(l.t)},${yFor(l.value)}`).join(' ');

    // Value labels on the non-best dots. Every one would collide at six months
    // and ~30 lifts, so a label is drawn only where it clears the ones already
    // placed - and the month bests, which always keep theirs. Tapping any dot
    // shows the exact figure regardless.
    const placed: { x: number; y: number }[] = bests.map((l) => ({
      x: xFor(l.t),
      y: yFor(l.value) - 10,
    }));
    const valueLabels: { key: string; x: number; y: number; text: string }[] = [];
    for (const l of lifts) {
      if (l.isMonthBest) continue;
      const x = xFor(l.t);
      const y = yFor(l.value) - 8;
      const clears = placed.every(
        (p) => Math.abs(p.x - x) >= VALUE_LABEL_W || Math.abs(p.y - y) >= VALUE_LABEL_H,
      );
      if (!clears) continue;
      placed.push({ x, y });
      valueLabels.push({ key: l.id, x, y, text: String(Math.round(l.value)) });
    }

    return (
      <svg
        className="trend-chart__svg"
        viewBox={`0 0 ${w} ${h}`}
        width={w}
        height={h}
        role="img"
        aria-label={ariaLabel}
      >
        {gridVals.map((v, idx) => {
          const y = yFor(v);
          return (
            <g key={`grid-${idx}`}>
              <line x1={PAD_L} y1={y} x2={w - PAD_R} y2={y} className="trend-chart__grid" />
              <text x={PAD_L - 6} y={y + 3} className="trend-chart__ylabel" textAnchor="end">
                {Math.round(v)}
              </text>
            </g>
          );
        })}

        {bests.length > 1 && (
          <polyline
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={linePoints}
          />
        )}

        {/* Ordinary sets: same hue, faded, so the month's best stays the headline. */}
        {lifts
          .filter((l) => !l.isMonthBest)
          .map((l) => (
            <circle
              key={`lift-${l.id}`}
              cx={xFor(l.t)}
              cy={yFor(l.value)}
              r={3}
              fill={color}
              fillOpacity={0.52}
              stroke="var(--color-paper-light)"
              strokeWidth={1}
              strokeOpacity={0.35}
            />
          ))}

        {valueLabels.map((l) => (
          <text
            key={`val-${l.key}`}
            x={l.x}
            y={Math.max(9, l.y)}
            textAnchor="middle"
            className="trend-chart__minorlabel"
          >
            {l.text}
          </text>
        ))}

        {/* Month bests, drawn over the faded dots. */}
        {bests.map((l) => (
          <circle
            key={`best-${l.id}`}
            cx={xFor(l.t)}
            cy={yFor(l.value)}
            r={5}
            fill={color}
            stroke="var(--color-paper-light)"
            strokeWidth={2}
          />
        ))}

        {bests.map((l) => (
          <text
            key={`bestval-${l.id}`}
            x={xFor(l.t)}
            y={Math.max(11, yFor(l.value) - 10)}
            textAnchor="middle"
            className="trend-chart__vallabel"
          >
            {Math.round(l.value)}
          </text>
        ))}

        {/* Selection ring, under the hit targets so it never eats the next tap. */}
        {lifts
          .filter((l) => l.id === selectedLiftId)
          .map((l) => (
            <circle
              key={`sel-${l.id}`}
              cx={xFor(l.t)}
              cy={yFor(l.value)}
              r={9}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeOpacity={0.9}
            />
          ))}

        {ticks.map((t, idx) => (
          <text
            key={`x-${idx}`}
            x={Math.min(w - PAD_R, Math.max(PAD_L, xFor(t.t)))}
            y={h - 6}
            textAnchor={t.t <= 0.02 ? 'start' : t.t >= 0.98 ? 'end' : 'middle'}
            className="trend-chart__xlabel"
          >
            {t.label}
          </text>
        ))}

        {/* Finger-sized targets last so they sit on top of every mark. Month
            bests come after the rest, so where targets overlap the bold dot -
            the one a user is most likely aiming at - wins. */}
        {onSelectLift &&
          [...lifts.filter((l) => !l.isMonthBest), ...bests].map((l) => {
            const cx = xFor(l.t);
            const cy = yFor(l.value);
            return (
              <circle
                key={`hit-${l.id}`}
                cx={cx}
                cy={cy}
                r={HIT_R}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Report the anchor in the wrapper's coordinates - SVG x/y are
                  // relative to the plot div, which is inset by the figure's
                  // padding - and clamped so the popup stays inside the chart.
                  const plot = plotRef.current;
                  onSelectLift(
                    l,
                    Math.min(Math.max(cx, POPUP_HALF_W), w - POPUP_HALF_W) +
                      (plot?.offsetLeft ?? 0),
                    cy + (plot?.offsetTop ?? 0),
                  );
                }}
              />
            );
          })}
      </svg>
    );
  };

  return (
    <figure className="trend-chart">
      <figcaption className="trend-chart__title">
        <span
          className="trend-chart__swatch"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
        {title}
        <span className="trend-chart__unit">estimated 1RM (lb)</span>
      </figcaption>

      {!hasData ? (
        <div className="trend-chart__empty">No {title.toLowerCase()} lifts logged yet.</div>
      ) : (
        <div className="trend-chart__plot" ref={plotRef}>
          {ready && renderPlot()}
        </div>
      )}
    </figure>
  );
}
